import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { moneyFormatFromCents } from '../../../utilities/moneyFormat';
import { backfillImportBalances, loadImportBalances } from '../../../redux/actions/import.js';
import importBatchName from '../../../utilities/importBatchName.js';
import Button from '../../components/Button.jsx';
import CircleLoader from '../../components/CircleLoader.jsx';

/** Keep the wording here in step with Balance_Reconciliation_Service's status constants. */
const STATUS_LABELS = {
	matched: 'Adds up',
	off: 'Does not add up',
	advisory: 'Expected drift',
	no_transactions: 'Balances only',
	not_comparable: 'Nothing to check',
};

export default function ImportBalanceReport() {
	const dispatch = useDispatch();
	const batch = useSelector((state) => state.importing.selectedBatch);
	const reconciliation = useSelector((state) => state.importing.reconciliation);
	const backfill = useSelector((state) => state.importing.backfill);
	const comparedAgainst = useSelector((state) => state.importing.comparedAgainst);
	const comparableBatches = useSelector((state) => state.importing.comparableBatches);
	const backfillWritten = useSelector((state) => state.importing.backfillWritten);
	const loading = useSelector((state) => state.importing.loadingBalances);
	const backfilling = useSelector((state) => state.importing.backfilling);

	if(!batch) {
		return null;
	}

	const writable = backfill.reduce((total, plan) => total + plan.writable, 0);
	const heldBack = backfill.reduce((total, plan) => total + plan.held_back, 0);
	const unmapped = backfill.filter((plan) => plan.state === 'unmapped').length;

	return (
		<div className='import-balances'>
			<h6>
				Balance history{' '}
				{(loading || backfilling) && <CircleLoader size='small' />}
			</h6>
			<p className='grey-text'>
				A balance export is one reading per account per day. It fills in the history for the
				stretch no sync covered, and it doubles as a check on a transaction import &mdash; if the
				transactions are complete, they have to add up to the balance change over the same span.
			</p>

			<Reconciliation
				rows={reconciliation}
				comparedAgainst={comparedAgainst}
				comparableBatches={comparableBatches}
				onCompareWith={(againstBatchId) => dispatch(loadImportBalances(batch.id, againstBatchId))}
			/>

			<h6>Backfill</h6>
			<p className='grey-text'>
				{writable > 0
					? <>
						{writable} daily {writable === 1 ? 'snapshot' : 'snapshots'} would be written.
						{heldBack > 0 && ` ${heldBack} ${heldBack === 1 ? 'day is' : 'days are'} left alone
							because the bank already reported on ${heldBack === 1 ? 'it' : 'them'}.`}
						{unmapped > 0 && ` ${unmapped} ${unmapped === 1 ? 'account is' : 'accounts are'}
							still waiting on a mapping above.`}
					</>
					: 'Nothing to write yet — map this file’s accounts above first.'
				}
			</p>

			<div className='import-table-scroll'>
				<table className='import-table'>
					<thead>
						<tr>
							<th>In the file</th>
							<th>Maps to</th>
							<th>Days</th>
							<th className='right-align'>To write</th>
							<th className='right-align'>Already known</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{backfill.map((plan) => (
							<BackfillRow key={plan.external_account} plan={plan} />
						))}
					</tbody>
				</table>
			</div>

			<div className='import-backfill-actions'>
				{backfillWritten !== null &&
				<span className='grey-text'>
					{backfillWritten} {backfillWritten === 1 ? 'snapshot' : 'snapshots'} written.
				</span>
				}
				<Button
					onClick={() => dispatch(backfillImportBalances(batch.id))}
					disabled={backfilling || writable === 0}
				>
					{backfilling ? 'Writing…' : 'Write balance history'}
				</Button>
			</div>
		</div>
	);
}

/**
 * The check itself. Read the difference column: on an ordinary account it should be zero, and what it
 * is when it is not zero is the size of what the transaction file is missing.
 */
function Reconciliation({ rows, comparedAgainst, comparableBatches, onCompareWith }) {
	if(!rows.length) {
		return null;
	}

	return (
		<div className='import-reconciliation'>
			<div className='import-reconciliation-source'>
				{comparedAgainst
					? <label>
						<span className='grey-text'>Checked against</span>
						<select
							className='browser-default'
							value={comparedAgainst.id}
							onChange={(event) => onCompareWith(Number(event.target.value))}
						>
							{comparableBatches.map((candidate) => (
								<option key={candidate.id} value={candidate.id}>
									{importBatchName(candidate)} ({candidate.row_count} rows)
								</option>
							))}
						</select>
					</label>
					: <span className='grey-text'>
						Upload a transaction export too and this file will check it.
					</span>
				}
			</div>

			<div className='import-table-scroll'>
				<table className='import-table'>
					<thead>
						<tr>
							<th>In the file</th>
							<th>Window</th>
							<th className='right-align'>Balance moved</th>
							<th className='right-align'>Transactions say</th>
							<th className='right-align'>Difference</th>
							<th>Result</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => (
							<tr key={row.external_account} className={`import-row-${row.status}`}>
								<td>
									<span className='import-external-key'>{row.external_account}</span>
									{!!row.account_name &&
									<div className='grey-text'>{row.account_name}</div>
									}
								</td>
								<td className='grey-text'>
									{row.start_date
										? <>{row.start_date} &ndash; {row.end_date}</>
										: <>{row.file_first_date} &ndash; {row.file_last_date}</>
									}
								</td>
								<td className='right-align'>
									{money(row.expected_change === null ? row.file_change : row.expected_change)}
								</td>
								<td className='right-align'>
									{row.actual_change === null
										? <span className='grey-text'>&mdash;</span>
										: money(row.actual_change)
									}
								</td>
								<td className='right-align'>
									{row.difference === null || row.difference === 0
										? <span className='grey-text'>&mdash;</span>
										: <strong>{money(row.difference)}</strong>
									}
								</td>
								<td>
									<span className={`import-status import-status-${row.status}`} title={row.reason}>
										{STATUS_LABELS[row.status] || row.status}
									</span>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

function BackfillRow({ plan }) {
	return (
		<tr className={`import-status-${plan.state}`}>
			<td>
				<span className='import-external-key'>{plan.external_account}</span>
			</td>
			<td>
				{plan.account_name
					? <>{plan.account_name} <span className='grey-text'>({plan.account_type})</span></>
					: <span className='grey-text'>
						{plan.state === 'skipped' ? 'Skipped' : 'Not mapped yet'}
					</span>
				}
			</td>
			<td className='grey-text'>{plan.first_date} &ndash; {plan.last_date}</td>
			<td className='right-align'>{plan.writable || <span className='grey-text'>&mdash;</span>}</td>
			<td className='right-align grey-text'>{plan.held_back || <>&mdash;</>}</td>
			<td>
				{!!plan.warning &&
				<span className='import-warning' title={plan.warning}>No credit limit on record</span>
				}
			</td>
		</tr>
	);
}

function money(cents) {
	if(cents === null || cents === undefined) {
		return <span className='grey-text'>&mdash;</span>;
	}
	return moneyFormatFromCents(cents);
}
